import { useEffect, useRef, useState } from 'react'
import { LeftOutlined, RightOutlined, RotateRightOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { Button } from '../../ui/button'
import { ToolTip } from '../../ui/tooltip'
import { Loader } from '../../ui/loader'
import css from './ui.recognition.module.scss'

export const RecognitionPreview = ({ file, page, onPageChange }: {
    file?: File; page: number; onPageChange: (page: number) => void
}) => {
    const [url, setUrl] = useState('')
    const [pdf, setPdf] = useState<PDFDocumentProxy>()
    const [pageCount, setPageCount] = useState(1)
    const [zoom, setZoom] = useState(1)
    const [rotation, setRotation] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [imageSize, setImageSize] = useState({ width: 1, height: 1 })
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        setZoom(1)
        setRotation(0)
        setError('')
        setImageSize({ width: 1, height: 1 })
        setPdf(undefined)
        setPageCount(1)
        setUrl('')
        if (!file) return
        const objectUrl = URL.createObjectURL(file)
        setUrl(objectUrl)
        let disposed = false
        let task: ReturnType<typeof import('pdfjs-dist')['getDocument']> | undefined
        if (/\.pdf$/i.test(file.name)) {
            setLoading(true)
            void import('pdfjs-dist').then(async module => {
                if (disposed) return
                module.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()
                const data = new Uint8Array(await file.arrayBuffer())
                if (disposed) return
                task = module.getDocument({ data, isEvalSupported: false })
                const document = await task.promise
                if (!disposed) { setPdf(document); setPageCount(document.numPages) }
            }).catch(() => { if (!disposed) setError('Не удалось открыть PDF.') })
                .finally(() => { if (!disposed) setLoading(false) })
        } else setLoading(false)
        return () => { disposed = true; void task?.destroy(); URL.revokeObjectURL(objectUrl) }
    }, [file])

    useEffect(() => {
        if (!pdf) return
        let disposed = false
        let render: ReturnType<Awaited<ReturnType<PDFDocumentProxy['getPage']>>['render']> | undefined
        setLoading(true)
        void pdf.getPage(Math.max(1, Math.min(page, pdf.numPages))).then(documentPage => {
            if (disposed || !canvasRef.current) return
            const viewport = documentPage.getViewport({ scale: 1.6, rotation: (documentPage.rotate + rotation) % 360 })
            const canvas = canvasRef.current
            canvas.width = viewport.width
            canvas.height = viewport.height
            render = documentPage.render({ canvas, viewport })
            return render.promise
        }).catch(() => { if (!disposed) setError('Не удалось отобразить страницу.') })
            .finally(() => { if (!disposed) setLoading(false) })
        return () => { disposed = true; render?.cancel() }
    }, [pdf, page, rotation])

    return <section className={css.preview} aria-label="Оригинал документа">
        <div className={css.previewTools}>
            <ToolTip title="Предыдущая страница"><Button variant="icon" size="sm" isDisabled={!pdf || page <= 1} onClick={() => onPageChange(page - 1)}><LeftOutlined aria-hidden="true" /><span className={css.srOnly}>Предыдущая страница</span></Button></ToolTip>
            <span className={css.pageNumber}>{Math.min(page, pageCount)} / {pageCount}</span>
            <ToolTip title="Следующая страница"><Button variant="icon" size="sm" isDisabled={!pdf || page >= pageCount} onClick={() => onPageChange(page + 1)}><RightOutlined aria-hidden="true" /><span className={css.srOnly}>Следующая страница</span></Button></ToolTip>
            <span className={css.separator} />
            <ToolTip title="Уменьшить"><Button variant="icon" size="sm" isDisabled={zoom <= 1 || !file} onClick={() => setZoom(value => Math.max(1, value - .25))}><ZoomOutOutlined aria-hidden="true" /><span className={css.srOnly}>Уменьшить</span></Button></ToolTip>
            <span className={css.pageNumber}>{Math.round(zoom * 100)}%</span>
            <ToolTip title="Увеличить"><Button variant="icon" size="sm" isDisabled={zoom >= 3 || !file} onClick={() => setZoom(value => Math.min(3, value + .25))}><ZoomInOutlined aria-hidden="true" /><span className={css.srOnly}>Увеличить</span></Button></ToolTip>
            <ToolTip title="Повернуть"><Button variant="icon" size="sm" isDisabled={!file} onClick={() => setRotation(value => (value + 90) % 360)}><RotateRightOutlined aria-hidden="true" /><span className={css.srOnly}>Повернуть</span></Button></ToolTip>
        </div>
        <div className={css.previewScroll}>
            <Loader isOpen={loading} backdrop />
            {error ? <p role="status" className={css.previewMessage}>{error}</p> : pdf ? <canvas ref={canvasRef} style={{ width: `${zoom * 100}%` }} aria-label={`Страница ${page}`} />
                : url && !/\.pdf$/i.test(file?.name || '') ? <div className={css.imageStage} style={{ width: `${zoom * 100}%`, aspectRatio: rotation % 180 ? imageSize.height / imageSize.width : imageSize.width / imageSize.height }}>
                    <img src={url} alt={file?.name}
                        style={{ transform: `translate(-50%, -50%) rotate(${rotation}deg)`, width: `${rotation % 180 ? imageSize.width / imageSize.height * 100 : 100}%` }}
                        onLoad={event => setImageSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
                        onError={() => setError('Не удалось открыть изображение.')} />
                </div> : !loading && <p className={css.previewMessage}>Оригинал пока недоступен</p>}
        </div>
    </section>
}